'use client';

import { IconChevronDownFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-down-filled';
import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { expandIconRecipe } from '../../styles/expand-icon.styles';
import { Anchor } from '../anchor';
import { TextButton } from '../text-button';
import { ShowHideRootContext } from './context/show-hide-root.context';
import { showHideButtonRecipe } from './styles/show-hide-button.styles';
import type { ShowHideButtonPropsInterface } from './types/show-hide-button.types';

export const ShowHideButton = forwardRef<HTMLButtonElement, ShowHideButtonPropsInterface>(
  (props: ShowHideButtonPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, onClick, ...rest } = props;

    const { appearance, isHidden, setIsHidden } = useContext(ShowHideRootContext);

    const onClickHandler: MouseEventHandler<HTMLButtonElement> = useCallback(
      (event) => {
        setIsHidden(!isHidden);
        onClick?.(event);
      },
      [isHidden, onClick, setIsHidden],
    );

    const iconStyles = css(expandIconRecipe.raw({ open: !isHidden }));

    if (appearance === 'inline') {
      return (
        <Anchor asChild data-c11n-component="ShowHide.Button" data-c11n-version={__C11N_VERSION__}>
          <button
            type="button"
            ref={forwardedRef}
            onClick={onClickHandler}
            aria-expanded={!isHidden}
            {...rest}
          >
            {children}
          </button>
        </Anchor>
      );
    }

    return (
      <TextButton.Root
        ref={forwardedRef}
        onClick={onClickHandler}
        aria-expanded={!isHidden}
        className={cx('c11n', css(showHideButtonRecipe.raw({ appearance }), cssProp), className)}
        data-c11n-component="ShowHide.Button"
        data-c11n-version={__C11N_VERSION__}
        {...rest}
      >
        <span className={cx('c11n', iconStyles)}>
          <TextButton.Icon size="sm" render={<IconChevronDownFilled />} />
        </span>
        <TextButton.Label>{children}</TextButton.Label>
      </TextButton.Root>
    );
  },
);

ShowHideButton.displayName = 'ShowHide.Button';
